import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, Chip, LoadingState, StatCard, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Plus, MapPin, House, MagnifyingGlass, PencilSimple, Trash,
    ClockCounterClockwise, IdentificationCard, Car, Wallet, CurrencyDollar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { PARKING_TYPES, PARKING_TYPE_MAP, VEHICLE_TYPES, VEHICLE_TYPE_MAP, SLOT_STATUS_VARIANTS } from "@/lib/parkingTypes";
import CreateSlotDialog from "@/components/parking/CreateSlotDialog";
import AllocateSlotDialog from "@/components/parking/AllocateSlotDialog";
import EditSlotDialog from "@/components/parking/EditSlotDialog";
import SlotHistoryDialog from "@/components/parking/SlotHistoryDialog";
import ParkingVehiclesTab from "@/components/parking/ParkingVehiclesTab";
import ParkingPassesTab from "@/components/parking/ParkingPassesTab";
import ParkingBillingTab from "@/components/parking/ParkingBillingTab";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function ParkingManagement() {
    const [slots, setSlots] = useState([]);
    const [flats, setFlats] = useState([]);
    const [summary, setSummary] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [filterBlock, setFilterBlock] = useState("all");
    const [filterType, setFilterType] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterVehicleType, setFilterVehicleType] = useState("all");
    const [filterPaid, setFilterPaid] = useState("all");
    const [filterPass, setFilterPass] = useState("all");

    const [createOpen, setCreateOpen] = useState(false);
    const [allocateSlot, setAllocateSlot] = useState(null);
    const [editSlot, setEditSlot] = useState(null);
    const [historySlot, setHistorySlot] = useState(null);

    async function loadSlots() {
        try {
            const { data } = await api.get("/parking/slots");
            setSlots(data || []);
        } catch (e) { toast.error(formatError(e)); }
    }
    async function loadSummary() {
        try {
            const { data } = await api.get("/parking/summary");
            setSummary(data);
        } catch (e) { toast.error(formatError(e)); }
    }
    async function loadFlats() {
        try {
            const { data } = await api.get("/flats");
            setFlats(data || []);
        } catch (e) { toast.error(formatError(e)); }
    }
    async function loadSettings() {
        try {
            const { data } = await api.get("/parking/settings");
            setSettings(data);
        } catch (e) { toast.error(formatError(e)); }
    }

    async function refreshAll() {
        await Promise.all([loadSlots(), loadSummary(), loadFlats(), loadSettings()]);
    }

    useEffect(() => {
        (async () => { setLoading(true); await refreshAll(); setLoading(false); })();
    }, []);

    function afterMutation() { loadSlots(); loadSummary(); }

    const blocks = useMemo(() => {
        const set = new Set(slots.map((s) => s.block).filter(Boolean));
        return Array.from(set).sort();
    }, [slots]);

    const filteredSlots = useMemo(() => {
        let rows = slots;
        if (filterBlock !== "all") rows = rows.filter((s) => s.block === filterBlock);
        if (filterType !== "all") rows = rows.filter((s) => s.parking_type === filterType);
        if (filterStatus !== "all") rows = rows.filter((s) => s.display_status === filterStatus);
        if (filterVehicleType !== "all") rows = rows.filter((s) => s.vehicle?.vehicle_type === filterVehicleType);
        if (filterPaid === "paid") rows = rows.filter((s) => s.is_paid_current);
        if (filterPaid === "free") rows = rows.filter((s) => !s.is_paid_current);
        if (filterPass !== "all") rows = rows.filter((s) => (filterPass === "none" ? !s.pass : s.pass?.status === filterPass));
        const term = search.trim().toLowerCase();
        if (term) {
            rows = rows.filter((s) => {
                const flatLabel = s.flat ? `${s.flat.block}-${s.flat.number}` : "";
                return s.slot_number.toLowerCase().includes(term) || flatLabel.toLowerCase().includes(term)
                    || (s.vehicle?.registration_number || "").toLowerCase().includes(term);
            });
        }
        return rows;
    }, [slots, filterBlock, filterType, filterStatus, filterVehicleType, filterPaid, filterPass, search]);

    async function releaseSlot(slot) {
        if (!window.confirm(`Release slot ${slot.slot_number} from ${slot.flat ? `${slot.flat.block}-${slot.flat.number}` : "its current flat"}?`)) return;
        try {
            await api.post(`/parking/slots/${slot.id}/release`, {});
            toast.success(`Slot ${slot.slot_number} released`);
            afterMutation();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function reserveSlot(slot) {
        try {
            await api.post(`/parking/slots/${slot.id}/reserve`, {});
            toast.success(`Slot ${slot.slot_number} reserved`);
            afterMutation();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function unreserveSlot(slot) {
        try {
            await api.post(`/parking/slots/${slot.id}/unreserve`);
            toast.success(`Slot ${slot.slot_number} unreserved`);
            afterMutation();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function deleteSlot(slot) {
        if (!window.confirm(`Delete slot ${slot.slot_number}? This cannot be undone.`)) return;
        try {
            await api.delete(`/parking/slots/${slot.id}`);
            toast.success("Parking slot deleted");
            afterMutation();
        } catch (e) { toast.error(formatError(e)); }
    }

    if (loading) return <LoadingState />;

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Gate"
                title="Parking Management"
                description="Slots, historical allocations, resident vehicles, passes and optional paid parking billing."
                actions={
                    <Button data-testid="create-slot-btn" onClick={() => setCreateOpen(true)} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                        <Plus size={16} className="mr-2" /> Create slot
                    </Button>
                }
            />

            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
                    <StatCard testId="stat-total" label="Total slots" value={summary.total} />
                    <StatCard testId="stat-available" label="Available" value={summary.available} />
                    <StatCard testId="stat-allotted" label="Allotted" value={summary.allotted} />
                    <StatCard testId="stat-reserved" label="Reserved" value={summary.reserved} />
                    <StatCard testId="stat-inactive" label="Inactive" value={summary.inactive} />
                    <StatCard testId="stat-vehicles" label="Active vehicles" value={summary.vehicles} />
                    <StatCard testId="stat-passes" label="Active passes" value={summary.active_passes} />
                    <StatCard testId="stat-charges-due" label="Charges due" value={inr(summary.parking_charges_due)} accent />
                </div>
            )}

            <Tabs defaultValue="slots">
                <TabsList className="bg-white border border-brand-line rounded-full p-1 mb-6">
                    <TabsTrigger data-testid="tab-slots" value="slots" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                        <MapPin size={14} className="mr-1.5" /> Slots
                    </TabsTrigger>
                    <TabsTrigger data-testid="tab-vehicles" value="vehicles" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                        <Car size={14} className="mr-1.5" /> Vehicles
                    </TabsTrigger>
                    <TabsTrigger data-testid="tab-passes" value="passes" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                        <IdentificationCard size={14} className="mr-1.5" /> Passes
                    </TabsTrigger>
                    <TabsTrigger data-testid="tab-billing" value="billing" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                        <Wallet size={14} className="mr-1.5" /> Billing
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="slots">
                    <div className="bg-white border border-brand-line rounded-sm p-4 mb-6 space-y-3">
                        <div className="flex items-center gap-3">
                            <MagnifyingGlass size={18} className="text-brand-inkSoft shrink-0" />
                            <Input data-testid="slots-search-input" placeholder="Search slot, flat or vehicle reg…" value={search} onChange={(e) => setSearch(e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-0" />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            <Select value={filterBlock} onValueChange={setFilterBlock}>
                                <SelectTrigger data-testid="filter-block-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Block" /></SelectTrigger>
                                <SelectContent><SelectItem value="all">All blocks</SelectItem>{blocks.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={filterType} onValueChange={setFilterType}>
                                <SelectTrigger data-testid="filter-parking-type-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Parking type" /></SelectTrigger>
                                <SelectContent><SelectItem value="all">All types</SelectItem>{PARKING_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={filterStatus} onValueChange={setFilterStatus}>
                                <SelectTrigger data-testid="filter-status-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Status" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All statuses</SelectItem>
                                    <SelectItem value="available">Available</SelectItem>
                                    <SelectItem value="allotted">Allotted</SelectItem>
                                    <SelectItem value="reserved">Reserved</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={filterVehicleType} onValueChange={setFilterVehicleType}>
                                <SelectTrigger data-testid="filter-vehicle-type-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Vehicle type" /></SelectTrigger>
                                <SelectContent><SelectItem value="all">All vehicle types</SelectItem>{VEHICLE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={filterPaid} onValueChange={setFilterPaid}>
                                <SelectTrigger data-testid="filter-paid-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Paid / Free" /></SelectTrigger>
                                <SelectContent><SelectItem value="all">Paid & Free</SelectItem><SelectItem value="paid">Paid only</SelectItem><SelectItem value="free">Free only</SelectItem></SelectContent>
                            </Select>
                            <Select value={filterPass} onValueChange={setFilterPass}>
                                <SelectTrigger data-testid="filter-pass-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Pass status" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All passes</SelectItem>
                                    <SelectItem value="active">Active pass</SelectItem>
                                    <SelectItem value="expired">Expired pass</SelectItem>
                                    <SelectItem value="cancelled">Cancelled pass</SelectItem>
                                    <SelectItem value="none">No pass</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {filteredSlots.length === 0 ? (
                        <EmptyState title="No parking slots found" description={slots.length === 0 ? "Create your first parking slot to get started." : "Try adjusting your filters."} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredSlots.map((slot) => {
                                const typeInfo = PARKING_TYPE_MAP[slot.parking_type];
                                const TypeIcon = typeInfo?.icon || MapPin;
                                return (
                                    <div key={slot.id} data-testid={`slot-card-${slot.id}`} className="bg-white border border-brand-line rounded-sm p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <TypeIcon size={22} weight="duotone" className="text-brand-action" />
                                                <div>
                                                    <p className="font-heading text-lg font-bold text-brand-ink">{slot.slot_number}</p>
                                                    <p className="text-xs text-brand-inkSoft">
                                                        {[typeInfo?.label, slot.block, slot.floor && `Floor ${slot.floor}`].filter(Boolean).join(" · ") || "No location set"}
                                                    </p>
                                                </div>
                                            </div>
                                            <Chip variant={SLOT_STATUS_VARIANTS[slot.display_status] || "neutral"}>{slot.display_status}</Chip>
                                        </div>

                                        {slot.flat && (
                                            <div className="mb-2 p-2 bg-brand-bg rounded-sm flex items-center justify-between text-sm">
                                                <span className="flex items-center gap-1 text-brand-ink font-medium"><House size={14} /> {slot.flat.block}-{slot.flat.number}</span>
                                                {slot.is_paid_current && <Chip variant="warn"><CurrencyDollar size={11} className="inline mr-0.5" />₹{slot.allocation?.charge_amount}/{slot.allocation?.frequency?.replace("_", " ")}</Chip>}
                                            </div>
                                        )}
                                        {slot.vehicle && (
                                            <p className="text-xs text-brand-inkSoft mb-1">Vehicle: <span className="font-mono text-brand-ink">{slot.vehicle.registration_number}</span> ({VEHICLE_TYPE_MAP[slot.vehicle.vehicle_type]?.label})</p>
                                        )}
                                        {slot.pass && (
                                            <p className="text-xs text-brand-inkSoft mb-1 flex items-center gap-1"><IdentificationCard size={12} /> Pass {slot.pass.pass_number} · {slot.pass.status}</p>
                                        )}
                                        {slot.notes && <p className="text-xs text-brand-inkSoft mt-1 italic">{slot.notes}</p>}

                                        <div className="flex gap-2 mt-3 flex-wrap">
                                            {slot.display_status === "available" && (
                                                <>
                                                    <Button size="sm" data-testid={`slot-allocate-btn-${slot.id}`} onClick={() => setAllocateSlot(slot)} className="rounded-full bg-green-600 hover:bg-green-700 text-white text-xs h-7">Allocate</Button>
                                                    <Button size="sm" variant="outline" data-testid={`slot-reserve-btn-${slot.id}`} onClick={() => reserveSlot(slot)} className="rounded-full border-brand-line text-xs h-7">Reserve</Button>
                                                </>
                                            )}
                                            {slot.display_status === "allotted" && (
                                                <>
                                                    <Button size="sm" variant="outline" data-testid={`slot-manage-btn-${slot.id}`} onClick={() => setAllocateSlot(slot)} className="rounded-full border-brand-line text-xs h-7">Manage charge</Button>
                                                    <Button size="sm" variant="outline" data-testid={`slot-release-btn-${slot.id}`} onClick={() => releaseSlot(slot)} className="rounded-full border-red-300 text-red-600 hover:bg-red-50 text-xs h-7">Release</Button>
                                                </>
                                            )}
                                            {slot.display_status === "reserved" && (
                                                <Button size="sm" variant="outline" data-testid={`slot-unreserve-btn-${slot.id}`} onClick={() => unreserveSlot(slot)} className="rounded-full border-brand-line text-xs h-7">Unreserve</Button>
                                            )}
                                            <Button size="sm" variant="outline" data-testid={`slot-history-btn-${slot.id}`} onClick={() => setHistorySlot(slot)} className="rounded-full border-brand-line text-xs h-7 w-7 p-0"><ClockCounterClockwise size={14} /></Button>
                                            <Button size="sm" variant="outline" data-testid={`slot-edit-btn-${slot.id}`} onClick={() => setEditSlot(slot)} className="rounded-full border-brand-line text-xs h-7 w-7 p-0"><PencilSimple size={14} /></Button>
                                            {slot.display_status === "available" && (
                                                <Button size="sm" variant="outline" data-testid={`slot-delete-btn-${slot.id}`} onClick={() => deleteSlot(slot)} className="rounded-full border-red-300 text-red-600 hover:bg-red-50 text-xs h-7 w-7 p-0"><Trash size={14} /></Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="vehicles"><ParkingVehiclesTab flats={flats} /></TabsContent>
                <TabsContent value="passes"><ParkingPassesTab slots={slots} /></TabsContent>
                <TabsContent value="billing"><ParkingBillingTab settings={settings} onSettingsSaved={loadSettings} /></TabsContent>
            </Tabs>

            <CreateSlotDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={afterMutation} />
            <AllocateSlotDialog open={!!allocateSlot} onOpenChange={(v) => !v && setAllocateSlot(null)} slot={allocateSlot} flats={flats} settings={settings} onSaved={afterMutation} />
            <EditSlotDialog open={!!editSlot} onOpenChange={(v) => !v && setEditSlot(null)} slot={editSlot} onSaved={afterMutation} />
            <SlotHistoryDialog open={!!historySlot} onOpenChange={(v) => !v && setHistorySlot(null)} slot={historySlot} />
        </div>
    );
}
